import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { type FC, Fragment, type RefObject, useCallback, useRef } from 'react';
import useEmitter from '@/hooks/use-emitter.ts';
import { AppEvent, type EventPayloads } from '@/types/event-emitter.types.ts';
import {
	editorStateToMarkdown,
	markdownToEditorState,
	plainTextToEditorState
} from '@/utils/editor-conversion';

type MarkdownFormattedViewPluginProps = {
	skipOnChange: RefObject<boolean>;
};

export const MarkdownFormattedViewPlugin: FC<
	MarkdownFormattedViewPluginProps
> = ({ skipOnChange }) => {
	const [editor] = useLexicalComposerContext();
	const lastFormattedStateRef = useRef<string | null>(null);
	const lastMarkdownViewValueRef = useRef<string | null>(null);

	const getSerializedEditorState = useCallback(
		() => editor.getEditorState().toJSON(),
		[editor]
	);

	const tryRestoreFormattedState = useCallback(
		(liveValue: string) => {
			if (
				!lastFormattedStateRef.current ||
				liveValue !== lastMarkdownViewValueRef.current
			) {
				return false;
			}

			try {
				skipOnChange.current = true;
				editor.setEditorState(
					editor.parseEditorState(lastFormattedStateRef.current)
				);
				return true;
			} catch {
				return false;
			}
		},
		[editor, skipOnChange]
	);

	const applySerializedViewState = useCallback(
		(isFormattedView: boolean, liveValue: string) => {
			try {
				const nextSerializedState = isFormattedView
					? markdownToEditorState(liveValue || '')
					: plainTextToEditorState(liveValue || '');

				skipOnChange.current = true;
				editor.setEditorState(
					editor.parseEditorState(JSON.stringify(nextSerializedState))
				);
			} catch (error) {
				console.error('Failed to apply serialized editor view state', {
					isFormattedView,
					error
				});
			}
		},
		[editor, skipOnChange]
	);

	const applyViewMode = useCallback(
		({
			isFormattedView
		}: EventPayloads[AppEvent.UPDATE_LEXICAL_EDITOR_VIEW_MODE]) => {
			const previousSerializedState = getSerializedEditorState();
			const liveValue = editorStateToMarkdown(previousSerializedState);

			if (!isFormattedView) {
				lastFormattedStateRef.current = JSON.stringify(previousSerializedState);
				lastMarkdownViewValueRef.current = liveValue;
			}

			if (isFormattedView && tryRestoreFormattedState(liveValue)) {
				return;
			}

			applySerializedViewState(isFormattedView, liveValue);
		},
		[
			getSerializedEditorState,
			tryRestoreFormattedState,
			applySerializedViewState
		]
	);

	useEmitter(AppEvent.UPDATE_LEXICAL_EDITOR_VIEW_MODE, applyViewMode);
	return <Fragment />;
};
